import { useCallback, useState } from 'react';

import type { ConfirmDialogProps } from './ConfirmDialog';

interface OpenDialogOptions<TData> {
  title?: React.ReactNode;
  message?: React.ReactNode;

  confirmActionTitle?: React.ReactNode;
  cancelActionTitle?: React.ReactNode;

  data?: TData;
}

export interface ConfirmDialogHookInput<TData> {
  onConfirm?: (data?: TData) => void;
  onCancel?: () => void;
}

export interface ConfirmDialogHookOutput<TData> {
  dialogProps: ConfirmDialogProps;
  onOpenDialog: (options: OpenDialogOptions<TData>) => void;
}

export default function useConfirmDialog<TData extends object>(input: ConfirmDialogHookInput<TData>): ConfirmDialogHookOutput<TData> {
  const { onConfirm, onCancel } = input;

  const [isOpen, setIsOpen] = useState(false);
  const [title, setTitle] = useState<React.ReactNode>();
  const [message, setMessage] = useState<React.ReactNode>();
  const [confirmActionTitle, setConfirmActionTitle] = useState<React.ReactNode>();
  const [cancelActionTitle, setCancelActionTitle] = useState<React.ReactNode>();
  const [data, setData] = useState<TData | undefined>();

  const handleConfirmAction = useCallback(() => {
    if (onConfirm) {
      onConfirm(data);
    }
    setIsOpen(false);
  }, [onConfirm, data]);

  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel();
    }
    setIsOpen(false);
  }, [onCancel]);

  const onOpenDialog = useCallback((options: OpenDialogOptions<TData>) => {
    setIsOpen(true);
    setTitle(options?.title);
    setMessage(options?.message);
    setConfirmActionTitle(options?.confirmActionTitle);
    setCancelActionTitle(options?.cancelActionTitle);
    setData(options?.data);
  }, []);

  return {
    dialogProps: {
      open: isOpen,
      title,
      message,

      confirmActionTitle,
      cancelActionTitle,

      onConfirm: onConfirm ? handleConfirmAction : undefined,
      onCancel: handleCancel,
    },
    onOpenDialog,
  };
}
